// ©2024 Austin App House. All rights reserved.
//
// Super-admin contractors list API (frontend contract).
//
// POST /api/admin/contractors
//   body:  page, perPage, sortBy, sortDirection, search, zipcode, range,
//          certificates (comma-separated ids), suspended ('all'|'active'|'suspended'),
//          createdAtFrom?, createdAtTo?, lastLoginAtFrom?, lastLoginAtTo? (YYYY-MM-DD)
//   res:   { payload: Contractor[], total: number }
//
// PATCH /api/admin/contractors/:contractorId/suspension
//   body:  { suspended: boolean, reason?: string, timeframe?: string }
//     - When suspended=true the backend should: persist reason + timeframe,
//       schedule an auto-lift timer for the timeframe, and email the contractor
//       (reason + duration). When suspended=false it should clear the timer and
//       email the contractor that the suspension was lifted.
//   res:   { success, message, suspended }
//
// PATCH /api/admin/contractors/:contractorId/deactivation
//   body:  { deactivated: boolean, reason?: string }
//     - When deactivated=true the backend should: set status='deactivated',
//       remove the contractor from the client-side "find contractors" list,
//       stop "work available" emails, block WO routing, and (optionally) email
//       the contractor with the provided reason. When deactivated=false it
//       returns them to active status.
//   res:   { success, message, deactivated }

import axios from 'axios';

export const ADMIN_CONTRACTORS = '/api/admin/contractors';

export function toYmd(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getContractorUserId(row) {
  return row?.userId || row?.user_id || row?._id;
}

export function formatAdminContractorDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('en-US');
}

export function isContractorDeactivated(row) {
  return Boolean(row?.deactivated) || String(row?.status || '').toLowerCase() === 'deactivated';
}

export function getContractorStatusLabel(row) {
  if (isContractorDeactivated(row)) return 'Deactivated';
  if (row?.suspended) return 'Suspended';
  if (row?.status) return row.status;
  return 'Active';
}

export async function fetchAdminContractors({
  page = 1,
  perPage = 15,
  sortBy = 'name',
  sortDirection = 1,
  search = '',
  zipcode = '',
  range,
  certificates = [],
  suspended = 'all',
  registeredDateRange = {},
  lastLoginRange = {},
}) {
  const params = {
    page,
    perPage,
    sortBy,
    sortDirection,
    suspended,
  };

  if (search?.trim()) {
    params.search = `"${search.trim().replaceAll(' ', '" "')}"`;
  }
  if (zipcode?.trim()) {
    params.zipcode = zipcode.trim();
  }
  if (range != null && !Number.isNaN(Number(range))) {
    params.range = range;
  }
  if (certificates?.length) {
    params.certificates = certificates.join(',');
  }

  const createdAtFrom = toYmd(registeredDateRange.from);
  const createdAtTo = toYmd(registeredDateRange.to);
  const lastLoginAtFrom = toYmd(lastLoginRange.from);
  const lastLoginAtTo = toYmd(lastLoginRange.to);

  if (createdAtFrom) params.createdAtFrom = createdAtFrom;
  if (createdAtTo) params.createdAtTo = createdAtTo;
  if (lastLoginAtFrom) params.lastLoginAtFrom = lastLoginAtFrom;
  if (lastLoginAtTo) params.lastLoginAtTo = lastLoginAtTo;

  const { data } = await axios.post(ADMIN_CONTRACTORS, params);
  return {
    payload: data?.payload ?? data?.contractors ?? [],
    total: data?.total ?? data?.count ?? 0,
  };
}

export async function updateContractorSuspension(contractorId, suspended, { reason, timeframe } = {}) {
  const body = { suspended: Boolean(suspended) };
  if (suspended) {
    if (reason != null) body.reason = reason;
    if (timeframe != null) body.timeframe = timeframe;
  }
  const { data } = await axios.patch(
    `${ADMIN_CONTRACTORS}/${contractorId}/suspension`,
    body,
  );
  return data;
}

export async function updateContractorDeactivation(contractorId, deactivated, { reason } = {}) {
  const body = { deactivated: Boolean(deactivated) };
  if (deactivated && reason != null) body.reason = reason;
  const { data } = await axios.patch(
    `${ADMIN_CONTRACTORS}/${contractorId}/deactivation`,
    body,
  );
  return data;
}
